import React, { useState, useEffect, useCallback, useMemo } from 'react'
import Wrapper from '../../components/Wrapper'
import { Helmet } from 'react-helmet'
import { Box, Typography, CircularProgress, Paper } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { historyRequest } from '../../store/actions/history'
import Api from '../../Api'
import { menu, shortFunction, operatorReport as opTr, history as historyTranslate } from '../../translate'
import { CALL_TYPES, CALL_DIRECTIONS, PERIODS } from '../../helpers/demo'
import { greenTheme } from '../../helpers/style'
import { toast } from 'react-toastify'
import moment from 'moment'
import ReportHistoryTable from './OperatorReport/ReportHistoryTable'
import ReportPeriodRow from './OperatorReport/ReportPeriodRow'
import { hasRecordingsListenOwnOnly } from '../../helpers/employeePermissions'
import { profileEmployeeRequest } from '../../store/actions/users'

export default function OperatorHistory() {
  const dispatch = useDispatch()
  const langIndex = +localStorage.getItem('atsLang') || 1
  const userData = useSelector((state) => state.users.userData) || {}
  const employeeData = useSelector((state) => state.users.employeeData)
  const historyData1 = useSelector((state) => state.history.historyData1)
  const historyStatus = useSelector((state) => state.history.historyStatus)
  const [calls, setCalls] = useState([])
  const [historyData, setHistoryData] = useState(null)
  const [historyFacetFields, setHistoryFacetFields] = useState(null)
  const [graphicStatusData, setGraphicStatusData] = useState(null)
  const [graphicDispositionData, setGraphicDispositionData] = useState(null)
  const [period, setPeriod] = useState('today')
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10))
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10))
  const [employees, setEmployees] = useState([])
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [operatorId, setOperatorId] = useState('')

  const restrictToOwnRecordings = useMemo(
    () => hasRecordingsListenOwnOnly(employeeData),
    [employeeData]
  )

  const loading = historyStatus === 'request'

  useEffect(() => {
    if (employeeData?.buddy && employeeData?.permission) return
    dispatch(profileEmployeeRequest())
  }, [dispatch, employeeData?.buddy, employeeData?.permission])

  useEffect(() => {
    const fromUser = userData?.name ?? userData?.id ?? ''
    setOperatorId(fromUser || '')
  }, [userData?.name, userData?.id])

  useEffect(() => {
    let cancelled = false
    Api.employeeGetEmployees()
      .then((res) => {
        if (cancelled) return
        const list = res?.data?.employees || []
        const unique = list.filter((emp, i, self) =>
          i === self.findIndex((e) => (e.buddy && e.buddy === emp.buddy) || (e.id && e.id === emp.id))
        )
        setEmployees(unique)
        if (operatorId && unique.length) {
          const match = unique.find((e) => e.buddy === operatorId || e.name === operatorId || String(e.id) === String(operatorId))
          if (match) setSelectedEmployee(match)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [operatorId])

  const fetchHistory = useCallback(() => {
    const startTime = dateFrom ? new Date(dateFrom + 'T00:00:00.000Z').toISOString() : undefined
    const endTime = dateTo ? new Date(dateTo + 'T23:59:59.999Z').toISOString() : undefined
    const ownBuddy = String(employeeData?.buddy || '').trim()
    const callerValue = restrictToOwnRecordings
      ? (ownBuddy || undefined)
      : String(employeeData?.buddy ?? selectedEmployee?.buddy ?? selectedEmployee?.name ?? selectedEmployee?.id ?? '').trim() || undefined
    const search = {
      startTime: startTime || undefined,
      endTime: endTime || undefined,
      ...(callerValue ? { caller: callerValue } : {}),
    }
    dispatch(historyRequest(500, 1, search))
  }, [dispatch, dateFrom, dateTo, selectedEmployee, employeeData?.buddy, restrictToOwnRecordings])

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod)
    if (newPeriod === 'today') {
      const t = new Date().toISOString().slice(0, 10)
      setDateFrom(t)
      setDateTo(t)
    } else if (newPeriod === 'week') {
      const d = new Date()
      setDateTo(d.toISOString().slice(0, 10))
      d.setDate(d.getDate() - 7)
      setDateFrom(d.toISOString().slice(0, 10))
    } else if (newPeriod === 'month') {
      const d = new Date()
      setDateTo(d.toISOString().slice(0, 10))
      d.setDate(d.getDate() - 30)
      setDateFrom(d.toISOString().slice(0, 10))
    }
  }

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  useEffect(() => {
    if (historyStatus === 'fail') {
      toast.error('Failed to load history')
    }
  }, [historyStatus])

  useEffect(() => {
    if (historyStatus !== 'success' || !historyData1) return
    const docs = historyData1?.response?.docs
    setHistoryData(historyData1)
    setHistoryFacetFields(
      historyData1?.facet_counts?.facet_fields ?? null
    )
    if (docs && Array.isArray(docs)) {
      const mappedCalls = docs.map((doc) => {
        const main = doc.items?.find((i) => i.uniqueid === doc.linkedid) || doc.items?.[0]
        if (!main) return null
        return {
          uniqueid: main.uniqueid || doc.linkedid,
          start: main.start,
          duration: main.duration,
          disposition: main.disposition,
          direction: main.status || main.direction || (main.line && main.line !== 'null' ? 'outgoing' : 'incoming'),
          status: main.status || (main.line && main.line !== 'null' ? 'Out Call' : 'Incoming Call'),
          rate: main.rate,
          extension: main.extension,
          destination: main.destination,
          in_num: main.in_num,
          ext_num: main.ext_num,
          isAudio: main.isAudio,
        }
      }).filter(Boolean)
      setCalls(mappedCalls)
    } else {
      setCalls([])
    }
  }, [historyStatus, historyData1, employeeData])

  const formatDuration = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '0:00'
    const m = Math.floor(Number(seconds) / 60)
    const s = Math.floor(Number(seconds) % 60)
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const formatDate = (isoOrStamp) => {
    if (!isoOrStamp) return '—'
    const m = typeof isoOrStamp === 'number' ? moment.unix(isoOrStamp) : moment(isoOrStamp)
    if (!m.isValid()) return '—'
    return m.subtract(4, 'hours').format('DD-MM-YYYY HH:mm:ss')
  }

  const getCallTypeLabel = (key) => {
    const t = CALL_TYPES.find((x) => x.key === key)
    return t ? opTr[t.labelKey]?.[langIndex] ?? key : key
  }

  const getDirectionLabel = (key) => {
    const d = CALL_DIRECTIONS.find((x) => x.key === key)
    return d ? opTr[d.labelKey]?.[langIndex] ?? key : key || '—'
  }

  const getCallTypeStyle = (typeKey) => {
    const t = CALL_TYPES.find((x) => x.key === typeKey)
    if (!t) return { bg: 'rgba(156, 163, 175, 0.2)', color: '#6b7280', border: '#9ca3af' }
    return { bg: `${t.color}26`, color: t.color, border: t.color }
  }

  const getPeriodLabel = (p) => {
    const item = PERIODS.find((x) => x.value === p)
    return item ? opTr[item.labelKey]?.[langIndex] ?? p : p
  }

  const handleEmployeeChange = useCallback((emp) => {
    if (restrictToOwnRecordings) return
    setSelectedEmployee(emp ?? null)
    setOperatorId(emp ? (emp.buddy || emp.name || emp.id) : '')
  }, [restrictToOwnRecordings])

  const title = historyTranslate.history1?.[langIndex] ?? opTr.history?.[langIndex] ?? 'History'

  return (
    <>
      <Helmet>
        <title>{title} - {menu.worker?.[langIndex] || 'Employee'}</title>
      </Helmet>
      <Wrapper>
          
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, minHeight: 200 }}>
              <CircularProgress size={48} sx={{ color: greenTheme?.primary || 'primary.main', mb: 2 }} />
              <Typography variant="body2" sx={{ color: greenTheme?.primary || 'text.secondary' }}>
                {shortFunction.operator_loading?.[langIndex] ?? 'Loading...'}
              </Typography>
            </Box>
          ) : (
            <ReportHistoryTable
              calls={calls}
              operator={selectedEmployee ?? employeeData}
              opTr={opTr}
              langIndex={langIndex}
              getCallTypeLabel={getCallTypeLabel}
              getDirectionLabel={getDirectionLabel}
              getCallTypeStyle={getCallTypeStyle}
              formatDate={formatDate}
              formatDuration={formatDuration}
              historyData={historyData}
              historyFacetFields={historyFacetFields}
              graphicStatusData={graphicStatusData}
              graphicDispositionData={graphicDispositionData}
              historyGraphicLoading={false}
              historyGraphicError={historyStatus === 'fail' ? 'Failed to load history' : null}
            />
          )}
      </Wrapper>
    </>
  )
}
